import { useState, useEffect, useMemo } from "react";
import {
  EuiPage,
  EuiPageBody,
  EuiTitle,
  EuiSpacer,
  EuiFlexGroup,
  EuiFlexItem,
  EuiButton,
  EuiCallOut,
  EuiLoadingSpinner,
  EuiEmptyPrompt,
  EuiIcon,
  EuiText,
} from "@elastic/eui";
import { useAnketaI18n } from "src/internationalization";
import { useApi } from "src/api";
import { useOpsanioCan } from "../can";
import { AnketaResourcesEnum, useAnketaAuth } from "src/auth";
import { GitLabMerge, OpsanioVersions, OpsanioWaves } from "src/questionnaires";
import { useToast } from "src/notifications";
import { defaultInitWave, OPSANIO_WAVES } from "./consts";
import { WaveDeploymentTimeline } from "./timeline";
import { OpsanioApplianceData } from "../appliance";

export const OpsanioWaveVersionManager = (): JSX.Element => {
  const { translate } = useAnketaI18n();
  const { Get, Put } = useApi();
  const can = useOpsanioCan();
  const { keycloak } = useAnketaAuth();
  const { addToast } = useToast();

  const [versions, setVersions] = useState<
    Record<OpsanioWaves, OpsanioVersions | null>
  >({
    dev: null,
    main: null,
    qa: null,
    wave1: null,
    wave2: null,
    wave3: null,
    debug: null,
  });
  const [appliances, setAppliances] =
    useState<Record<OpsanioWaves, Array<string> | null>>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [tags, setTags] = useState({});
  const [merges, setMerges] =
    useState<Record<OpsanioWaves, GitLabMerge | null>>(null);

  const canFetch = useMemo<boolean>(() => {
    if (keycloak) {
      if (typeof keycloak.token === "string") {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  }, [keycloak]);

  const loadAllVersions = async (
    updateLoadingState: boolean = false,
  ): Promise<void> => {
    if (canFetch) {
      if (updateLoadingState) {
        setLoading(true);
        setError(null);
      }
      const loadedVersions: Record<string, OpsanioVersions | null> = {};

      // Waves are independent rows - load them concurrently, not one by one.
      await Promise.all(
        OPSANIO_WAVES.map(async (wave) => {
          try {
            const { data, error, status } = await Get({
              path: `v2/${AnketaResourcesEnum.opsanio}/versions/${wave}`,
              responseType: "json",
              token: keycloak.token,
            });
            if (error && status !== 404) {
              addToast({
                title: translate("anketa.opsanio.waves.fetch.error.title", {
                  defaultMessage: "Fehler beim pullen der Versionen!",
                }),
                color: "danger",
                text: error.message,
              });
            }
            // Seed defaults ONLY when the wave has no row yet (404). Any
            // other failure (500, timeout, 403) must not overwrite the real
            // pinned versions - it just leaves this wave unloaded.
            loadedVersions[wave] = null;
            if (data !== null) {
              loadedVersions[wave] = data as OpsanioVersions;
            } else if (
              status === 404 &&
              can(AnketaResourcesEnum.opsanioUpdates, "update")
            ) {
              switch (wave) {
                case "dev":
                  loadedVersions[wave] = {
                    ...defaultInitWave,
                    parentWave: "dev",
                    opsanio_plugin_version: "test",
                    pullPolicy: "Always",
                    coredns_version: "test",
                    codex: "test",
                    imap_wodle: "test",
                    kubernetes_wodle: "test",
                    osp_wodle: "test",
                    promtail_wodle: "test",
                    remotelog_wodle: "test",
                    semaphrone: "test",
                    snmp_wodle: "test",
                    opsanio_plugin_tag: `${loadedVersions[wave]?.opensearch_dashboard_version}-${loadedVersions[wave]?.wazuh_version}-test`,
                    wazuh_rules: "test",
                    webhook_wodle: "test",
                    netflow_wodle: "test",
                    most: "test",
                    cardea: "test",
                    apt_ring: "dev",
                  };
                  break;
                case "main":
                  loadedVersions[wave] = {
                    ...defaultInitWave,
                    pullPolicy: "Always",
                    parentWave: "dev",
                    opsanio_plugin_version: "1.12.0",
                    coredns_version: "latest",
                    codex: "latest",
                    opsanio_plugin_tag: `${loadedVersions[wave]?.opensearch_dashboard_version}-${loadedVersions[wave]?.wazuh_version}-1.12.0`,
                    imap_wodle: "latest",
                    kubernetes_wodle: "latest",
                    osp_wodle: "latest",
                    promtail_wodle: "latest",
                    remotelog_wodle: "latest",
                    semaphrone: "latest",
                    snmp_wodle: "latest",
                    wazuh_rules: "latest",
                    webhook_wodle: "latest",
                    netflow_wodle: "latest",
                    most: "latest",
                    cardea: "latest",
                    apt_ring: "",
                  };
                  break;
                case "qa":
                case "wave1":
                case "wave2":
                case "wave3":
                case "debug":
                  loadedVersions[wave] = {
                    ...defaultInitWave,
                    parentWave:
                      wave === "qa"
                        ? "main"
                        : (OPSANIO_WAVES[
                            OPSANIO_WAVES.indexOf(wave) - 1
                          ] as OpsanioWaves),
                    pullPolicy: "IfNotPresent",
                    opsanio_plugin_version: "1.11.4",
                    coredns_version: "latest",
                    opsanio_plugin_tag: `${loadedVersions[wave]?.opensearch_dashboard_version}-${loadedVersions[wave]?.wazuh_version}-1.11.4`,
                    codex: "v1.0.26",
                    most: "v1.0.15",
                    cardea: "v1.1.9",
                    apt_ring: "",
                    semaphrone: "v1.0.21",
                    wazuh_rules: "v1.0.4",
                    imap_wodle: "v1.0.22",
                    kubernetes_wodle: "v1.0.20",
                    netflow_wodle: "v1.0.22",
                    osp_wodle: "v1.0.76",
                    promtail_wodle: "v1.0.18",
                    remotelog_wodle: "v1.0.19",
                    snmp_wodle: "v1.0.21",
                    webhook_wodle: "v1.0.22",
                  };
                  break;
                default:
                  loadedVersions[wave] = null;
                  break;
              }

              if (loadedVersions[wave]) {
                const { error: postError } = await Put({
                  path: `v2/${AnketaResourcesEnum.opsanio}/versions/${wave}`,
                  value: loadedVersions[wave],
                  token: keycloak.token,
                });
                if (postError) {
                  console.error(postError);
                  addToast({
                    title: translate("anketa.opsanio.waves.post.error.title", {
                      defaultMessage:
                        "Fehler beim initialen anlegen der Versionen!",
                    }),
                    color: "danger",
                    text: postError.message,
                  });
                }
              }
            }
          } catch (err) {
            console.error(`Error loading ${wave}:`, err);
            loadedVersions[wave] = null;
          }
        }),
      );

      setVersions(
        loadedVersions as Record<OpsanioWaves, OpsanioVersions | null>,
      );
      if (updateLoadingState) {
        setLoading(false);
      }
    }
  };

  const getTags = async (updateLoadingState: boolean = false) => {
    if (canFetch) {
      if (updateLoadingState) {
        setLoading(true);
        setError(null);
      }
      const { data, error } = await Get({
        path: `v2/${AnketaResourcesEnum.opsanio}/gitlab/tags`,
        responseType: "json",
        token: keycloak.token,
      });
      if (error) {
        addToast({
          title: translate("anketa.opsanio.gitlab.toke.fetch.error.title", {
            defaultMessage: "Fehler beim pullen der Tags!",
          }),
          color: "danger",
          text: error.message,
        });
      }
      setTags(data);
      if (updateLoadingState) {
        setLoading(false);
      }
    }
  };

  const getMerges = async (updateLoadingState: boolean = false) => {
    if (canFetch) {
      if (updateLoadingState) {
        setLoading(true);
        setError(null);
      }
      const { data, error } = await Get({
        path: `v2/${AnketaResourcesEnum.opsanio}/gitlab/flux/merge`,
        responseType: "json",
        token: keycloak.token,
      });
      if (error) {
        addToast({
          title: translate("anketa.opsanio.gitlab.merge.fetch.error.title", {
            defaultMessage: "Fehler beim pullen der Merges!",
          }),
          color: "danger",
          text: error.message,
        });
      }
      // null means "still loading" to the timeline. Once the request is done
      // - failed, or no open merge requests (empty answer) - hand it an empty
      // record so it renders instead of spinning forever.
      setMerges((data ?? {}) as Record<OpsanioWaves, GitLabMerge | null>);
      if (updateLoadingState) {
        setLoading(false);
      }
    }
  };

  const getAppliances = async (updateLoadingState: boolean = false) => {
    if (canFetch) {
      if (updateLoadingState) {
        setLoading(true);
        setError(null);
      }
      // This view only needs each appliance's wave + VPC name. The /appliances
      // endpoint runs the full catalog parse (NetBox device lookups, version
      // resolution, Keycloak group provisioning) per questionnaire, which it does
      // not use; /appliances/summary derives wave + common from content parsing
      // (plus cached contact lookups) and skips all the external I/O.
      const { data, error } = await Get({
        path: `v2/${AnketaResourcesEnum.opsanio}/appliances/summary`,
        responseType: "json",
        token: keycloak.token,
      });
      if (error) {
        addToast({
          title: translate(`anketa.opsanio.appliance.fetch.error`),
          color: "danger",
          text: error.message,
        });
      }
      const releventEnvs: Record<OpsanioWaves, string[]> = {} as Record<
        OpsanioWaves,
        string[]
      >;

      (data as Array<OpsanioApplianceData>).forEach((d) => {
        // The update view only tracks the active fleet; archived appliances are
        // excluded from the per-wave deployment counts.
        if (d.archived) return;

        const wave = d.wave as OpsanioWaves;
        const vpc = d.common?.metadata?.name;

        if (!wave || !vpc) return;

        if (!releventEnvs[wave]) {
          releventEnvs[wave] = [];
        }

        if (!releventEnvs[wave].includes(vpc)) {
          releventEnvs[wave].push(vpc);
        }
      });

      setAppliances(releventEnvs);
      if (updateLoadingState) {
        setLoading(false);
      }
    }
  };

  const loadAllData = async (updateLoadingState: boolean = false) => {
    if (canFetch) {
      if (updateLoadingState) {
        setLoading(true);
        setError(null);
      }
      // These four fetches are independent; run them concurrently so the page
      // waits on the slowest one rather than the sum of all four.
      await Promise.all([
        loadAllVersions(),
        getTags(),
        getMerges(),
        getAppliances(),
      ]);
      if (updateLoadingState) {
        setLoading(false);
      }
    }
  };
  useEffect(() => {
    loadAllData(true);
  }, [canFetch]);

  if (loading) {
    return (
      <EuiPage paddingSize="l">
        <EuiPageBody>
          <EuiEmptyPrompt
            icon={<EuiLoadingSpinner size="xl" />}
            title={
              <h2>
                {translate("anketa.opsanio.loading", {
                  defaultMessage: "Lade Versionen...",
                })}
              </h2>
            }
          />
        </EuiPageBody>
      </EuiPage>
    );
  }

  return (
    <EuiPage paddingSize="l">
      <EuiPageBody>
        <EuiFlexGroup
          justifyContent="spaceBetween"
          alignItems="flexStart"
          gutterSize="m"
        >
          <EuiFlexItem grow>
            <EuiFlexGroup gutterSize="m" alignItems="center" responsive={false}>
              <EuiFlexItem grow={false}>
                <EuiIcon type="managementApp" size="xl" color="primary" />
              </EuiFlexItem>
              <EuiFlexItem grow={false}>
                <EuiTitle size="l">
                  <h1>
                    {translate("anketa.opsanio.title", {
                      defaultMessage: "Opsanio Wave Version Manager",
                    })}
                  </h1>
                </EuiTitle>
                <EuiText size="s" color="subdued">
                  {translate("anketa.opsanio.subtitle", {
                    defaultMessage:
                      "Promote component versions through the deployment waves, from dev to production.",
                  })}
                </EuiText>
              </EuiFlexItem>
            </EuiFlexGroup>
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            <EuiButton
              iconType="refresh"
              onClick={() => loadAllData(true)}
              isLoading={loading}
            >
              {translate("anketa.opsanio.refresh", {
                defaultMessage: "Aktualisieren",
              })}
            </EuiButton>
          </EuiFlexItem>
        </EuiFlexGroup>

        <EuiSpacer size="l" />

        {error && (
          <>
            <EuiCallOut
              title={translate("anketa.opsanio.error.loading", {
                defaultMessage: "Fehler beim Laden",
              })}
              color="danger"
              iconType="alert"
            >
              <p>{error}</p>
            </EuiCallOut>
            <EuiSpacer />
          </>
        )}

        <WaveDeploymentTimeline
          versions={versions}
          merges={merges}
          tags={tags}
          updateAction={loadAllData}
          stages={appliances}
        />
      </EuiPageBody>
    </EuiPage>
  );
};
